import * as _ from 'lodash';

import { hasServerError } from 'utils/utils';
import { prettifySize } from 'utils/prettify';


const uploaderPath = '/assets/img/dam/uploader';
const smallPath = '/assets/img/dam/small';
const bigPath = '/assets/img/dam/big';
const validTypes = [
  {extension: 'jpg', fileType: 'JPG', category: 'image', default: ''},
  {extension: 'jpeg', fileType: 'JPG', category: 'image', default: ''},
  {extension: 'png', fileType: 'PNG', category: 'image', default: ''},
  {extension: 'gif', fileType: 'GIF', category: 'image', default: ''},
  {extension: 'tif', fileType: 'TIF', category: 'image', default: ''},
  {extension: 'tiff', fileType: 'TIF', category: 'image', default: ''},
  {extension: 'bmp', fileType: 'BMP', category: 'image', default: ''},
  {extension: 'svg', fileType: 'SVG', category: 'image', default: ''},
  {extension: 'flv', fileType: 'FLV', category: 'video', default: 'flv.png'},
  {extension: 'f4v', fileType: 'F4V', category: 'video', default: 'f4v.png'},
  {extension: 'avi', fileType: 'AVI', category: 'video', default: 'avi.png'},
  {extension: 'webm', fileType: 'WEBM', category: 'video', default: 'webm.png'},
  {extension: 'ogv', fileType: 'OGV', category: 'video', default: 'ogv.png'},
  {extension: 'mp4', fileType: 'MP4', category: 'video', default: 'mp4.png'},
  {extension: 'mkv', fileType: 'MKV', category: 'video', default: 'mkv.png'},
  {extension: 'aif', fileType: 'AIF', category: 'audio', default: 'aif.png'},
  {extension: 'wav', fileType: 'WAV', category: 'audio', default: 'wav.png'},
  {extension: 'wma', fileType: 'WMA', category: 'audio', default: 'wma.png'},
  {extension: 'ogg', fileType: 'OGG', category: 'audio', default: 'ogg.png'},
  {extension: 'pcm', fileType: 'PCM', category: 'audio', default: 'pcm.png'},
  {extension: 'ac3', fileType: 'AC3', category: 'audio', default: 'ac3.png'},
  {extension: 'midi', fileType: 'MIDI', category: 'audio', default: 'midi.png'},
  {extension: 'mp3', fileType: 'MP3', category: 'audio', default: 'mp3.png'},
  {extension: 'aac', fileType: 'AAC', category: 'audio', default: 'aac.png'},
  {extension: 'pdf', fileType: 'PDF', category: 'document', default: 'pdf.png'},
  {extension: 'doc', fileType: 'DOC', category: 'document', default: 'doc.png'},
  {extension: 'xls', fileType: 'XLS', category: 'document', default: 'xls.png'},
  {extension: 'eps', fileType: 'EPS', category: 'document', default: 'eps.png'},
  {extension: 'txt', fileType: 'TXT', category: 'document', default: 'txt.png'},
];
export const invalidNameSymbols = ['&', '#', '\\', '/', ':', '*', '"', '<', '>', '|', '#', '%'];

export const pagAssetType = 'PAG';

export interface DigitalAsset {
  itemId?: string;
  file_type: string;
  filename: string;
  fileimg?: string;
  created_at?: string;
  dimensions?: {
    width: number,
    height: number,
    uom: string,
  };
  filesize: string;
  photo?: string;
  file?: any;
  isDuplicate?: boolean;
  isValidName?: boolean;
  hideStatus?: boolean;
  duplicate?: boolean;
  uploadStatus?: number;
}

export interface DigitalAssetRow extends DigitalAsset {
  selected?: boolean;
}

export const getExtension = (filename: string) => {
    const re = /(?:\.([^.]+))?$/;
    const ext = re.exec(filename)[1];

    return (ext) ? ext : '';
};

export const getName = (filename: string) => {
    const ext = getExtension(filename);
    const name = filename.replace(ext, '');
    return name ? name.substr(0, name.length - 1) : '';
};

export const isValidAssetType = (filename: string) => {
    const ext = getExtension(filename);
    const validType =  _.find(validTypes, ['extension', ext.toLowerCase()]);
    return ext && validType;
};

export const isValidAssetName = (filename: string) => {
    const name = getName(filename);
    let isValid = true;
    _.forEach(invalidNameSymbols, symbol => {
      isValid = isValid && name.indexOf(symbol) < 0;
    });
    return isValid;
};

export const getAssetFileType = (asset: any = {}) => {
  if (!asset) { return ''; }
  if (asset.file_type) { return asset.file_type; }
  if (!asset.filename) { return ''; }
  const ext = getExtension(asset.filename);
  if (ext) {
      const validType =  _.find(validTypes, ['extension', ext.toLowerCase()]);
      if (validType) {
        return validType.fileType;
      }
  }
  return '';
};

export const getAssetCategory = (asset: any) => {
  if (!asset) { return ''; }
  if (!asset.filename) { return ''; }
  const ext = getExtension(asset.filename);
  if (ext) {
      const validType =  _.find(validTypes, ['extension', ext.toLowerCase()]);
      if (validType) {
        return validType.category;
      }
  }
  return '';
};

export const getUploaderAssetThumbnail = (asset: any) => {
  const assetCategory = getAssetCategory(asset);
  switch (assetCategory) {
    case 'image':
      return asset.thumbnail;
    case 'video':
      return `${uploaderPath}/video.png`;
    case 'audio':
      return `${uploaderPath}/audio.png`;
    default:
      return `${uploaderPath}/file.png`;
  }
};

export const getAssetThumbnail = (asset: any, size: string = 'small') => {
  if (!(asset && asset.filename)) { return ''; }

  const ext = getExtension(asset.filename);

  if (ext) {
      const validType =  _.find(validTypes, ['extension', ext.toLowerCase()]);
      if (validType) {
        if (!validType.default) {
          if (size === 'big') { return asset.medium_thumbnail; } else { return asset.thumbnail; }
        } else {
          if (size === 'big') {
            return `${bigPath}/${validType.default}`;
          }
          return `${smallPath}/${validType.default}`;
        }
      }
  }
  // TODO do we have to support invalid types?
  return asset.thumbnail ? asset.thumbnail : '';
};

export const isImageFileType = (asset: any) => {
  return getAssetCategory(asset) === 'image';
};

export const isAssetItemShown = (asset: any, item: any): boolean => {
  if (item.item_name === '_id' ||
      item.item_name === 'origin_id' ||
      item.item_name === 'system_filename' ||
      item.item_name === 'digital_asset_id'
  ) { return false; }
  if (asset && asset.third_party_asset === 1 && (item.item_name === 'width' || item.item_name === 'height' || item.item_name === 'uom')) {
    return true;
  }
  return isImageFileType(asset) ? true : !item.imageOnly;
};

export function createDigitalAsset(file: any): DigitalAsset {
  const extIndex = file.name.lastIndexOf('.');
  const digitalAsset: DigitalAsset =  {
    filename: file.name,
    file_type: file.name.substring(extIndex + 1).toUpperCase(),
    filesize: prettifySize(file.size),
    fileimg: URL.createObjectURL(file),
    file,
    isValidName: isValidAssetName(file.name),
  };
  return digitalAsset;
}

const getErrorPath = (name: string, index: Number): string => {
  return index === 0 ? `digitalAssets.${name}` : `digitalAssets.${index}.${name}`;
};

export const setErrors = (items: Array<any>, segmentData: any, validation: any): void => {
  // set errors for digital assets
  _.forEach(items, (asset: any, index: Number) => {
    _.forEach(segmentData, (item: any) => {
      const path = getErrorPath(item.item_name, index);
      if (hasServerError(validation, path)) {
        asset.hasServerError = true;
        return;
      }
    });
    if (asset.hasServerError) {
      return;
    }
  });
};

export const isPAG = (asset: any): boolean => {
  return _.get(asset, 'asset_type') === pagAssetType;
};

export const isThirdPartyAsset = (asset: any): boolean => {
  return _.get(asset, 'third_party_asset') === 1;
};

export const createEmptyPAGAsset = (createId?: boolean): any => {
  return {
    asset_type: pagAssetType,
    filename: '',
    file_path: '',
    _id: createId ? _.uniqueId('padbID_') : null,
  };
};
